'use client'

import * as React from 'react'
import { useParams } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useIsLoggedInQuery } from 'auth/hooks'
import { Button } from '@repo/design-system-cf/components/button'
import { IconAdd } from '@repo/design-system-cf/components/icons'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'
import {
  GetImagesQueryRootKey,
  getImagesFn,
  useListInfiniteQuery,
} from '@web/gen/service/creativefabrica/backgroundremover/v2'
import { getSessionImagesVariables } from './session-images-query'

const SESSION_LIST_STALE_TIME_MS = 5 * 60 * 1000

function prefetchSessionImages(queryClient: ReturnType<typeof useQueryClient>, sessionId: string) {
  const variables = getSessionImagesVariables(sessionId)
  const { pagination: _, ...otherVariables } = variables
  void queryClient.prefetchInfiniteQuery({
    // oxlint-disable-next-line oxlint-plugin-query/exhaustive-deps -- generated infinite key hashes otherVariables (request minus pagination)
    queryKey: [GetImagesQueryRootKey, JSON.stringify(otherVariables)],
    initialPageParam: undefined as string | undefined,
    queryFn: () => getImagesFn(variables),
    staleTime: SESSION_LIST_STALE_TIME_MS,
  })
}

export function SessionRail() {
  const t = useTranslations('pages.background-remover')
  const params = useParams<{ id?: string }>()
  const activeSessionId = params?.id
  const queryClient = useQueryClient()
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const listQuery = useListInfiniteQuery(
    { pagination: { take: 20 } },
    {
      enabled: Boolean(isLoggedIn) && process.env.NODE_ENV !== 'test',
      staleTime: SESSION_LIST_STALE_TIME_MS,
    },
  )

  const sessions =
    listQuery.data?.pages.flatMap(page => page.sessions ?? []).filter(session => session.id) ?? []

  const isHome = !activeSessionId

  return (
    <nav
      aria-label={t('history')}
      className="flex h-[54px] w-full shrink-0 flex-row items-center gap-2 overflow-x-auto border-b border-cf-grey-200 bg-cf-grey-0 px-2 md:h-auto md:w-[88px] md:flex-col md:overflow-y-auto md:overflow-x-hidden md:border-b-0 md:border-r md:py-3"
    >
      {isHome ? (
        <Button variant="primary" size="small" disabled aria-label={t('new_session')}>
          <IconAdd className="h-5 w-5" />
        </Button>
      ) : (
        <Button variant="primary" size="small" asChild>
          <Link
            href={AppRoutes.TOOLS_BACKGROUND_REMOVER}
            component="Link"
            aria-label={t('new_session')}
          >
            <IconAdd className="h-5 w-5" />
          </Link>
        </Button>
      )}

      {sessions.map((session, index) => {
        const href = `${AppRoutes.TOOLS_BACKGROUND_REMOVER}${session.id}`
        const isActive = session.id === activeSessionId
        return (
          <Link
            key={session.id}
            href={href}
            component="Link"
            aria-current={isActive ? 'page' : undefined}
            onMouseEnter={() => {
              if (session.id) {
                prefetchSessionImages(queryClient, session.id)
              }
            }}
            className={`relative block size-12 shrink-0 overflow-hidden rounded-2 border ${
              isActive ? 'border-cf-purple-900 ring-2 ring-cf-purple-200' : 'border-cf-grey-200'
            }`}
          >
            {session.thumbnailUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={session.thumbnailUrl}
                alt=""
                width={48}
                height={48}
                decoding="async"
                loading={index < 8 ? 'eager' : 'lazy'}
                className="size-full object-cover"
              />
            ) : (
              <span className="block size-full bg-cf-grey-100" />
            )}
            {session.images && session.images > 1 ? (
              <span className="absolute bottom-0.5 right-0.5 rounded bg-cf-grey-1000 px-1 text-[10px] font-semibold text-cf-grey-0">
                {session.images}
              </span>
            ) : null}
          </Link>
        )
      })}
    </nav>
  )
}

export function BackgroundRemoverFrame({ children }: { children: React.ReactNode }) {
  return (
    <section className="flex min-h-[min(70vh,720px)] w-full flex-col md:flex-row">
      <SessionRail />
      <div className="min-h-0 min-w-0 flex-1">{children}</div>
    </section>
  )
}
